import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  SparklesIcon,
  LegalIcon,
  UsersIcon,
  HelpCircleIcon,
  StarIcon,
  FileTextIcon,
  LightbulbIcon,
  MicrophoneIcon,
} from "@/components/Icons";
import { ALL_PERSONAS, Persona } from "@/models/onboardingPersona";

type IconType = (props: { size?: number; className?: string }) => ReactNode;

// Each persona pairs with an icon + theme color. Colors keep the picker
// visually distinct without leaning on emoji.
const PERSONA_ICONS: Record<Persona, { Icon: IconType; tint: string }> = {
  "content-creator": { Icon: SparklesIcon, tint: "text-pink-500" },
  medical: { Icon: StarIcon, tint: "text-rose-500" },
  lawyer: { Icon: LegalIcon, tint: "text-blue-600" },
  "coach-tutor": { Icon: LightbulbIcon, tint: "text-amber-500" },
  "hearing-loss": { Icon: MicrophoneIcon, tint: "text-cyan-500" },
  "it-specialist": { Icon: FileTextIcon, tint: "text-indigo-500" },
  student: { Icon: FileTextIcon, tint: "text-violet-500" },
  business: { Icon: UsersIcon, tint: "text-emerald-500" },
  other: { Icon: HelpCircleIcon, tint: "text-[var(--text-tertiary)]" },
};

type Props = {
  actionId?: string;
  value: Persona | null;
  onChange: (persona: Persona) => void;
};

const PersonaPicker = ({ value, onChange }: Props) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2 w-full">
      {ALL_PERSONAS.map((persona) => {
        const { Icon, tint } = PERSONA_ICONS[persona];
        const isSelected = value === persona;
        return (
          <button
            key={persona}
            data-action-id="onboarding.persona.select"
            onClick={() => onChange(persona)}
            className={`flex items-center gap-3 px-4 py-3.5 rounded-2xl transition-all duration-200 active:scale-[0.98] w-full text-start ${
              isSelected
                ? "selected-surface scale-[1.01]"
                : "glass-tile"
            }`}
          >
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                isSelected
                  ? "bg-[var(--accent-bg)]"
                  : "bg-[var(--fill-1)]"
              }`}
            >
              <Icon
                size={18}
                className={isSelected ? "text-[var(--accent)]" : tint}
              />
            </div>
            <span
              className={`text-[15px] font-semibold ${
                isSelected
                  ? "text-[var(--text-primary)]"
                  : "text-[var(--text-secondary)]"
              }`}
            >
              {t(`onboarding.persona.label_${persona}`)}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default PersonaPicker;
